import { Box, Button, ButtonGroup, Flex, FormControl, FormLabel, IconButton, Input, Spinner } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import type { SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { useDebouncedSave } from "../hooks/useDebouncedSave";
import { useSaveField } from "../hooks/useSaveField";

interface DescriptionEntry {
  id: string;
  value: string;
}

const toEntries = (descriptions: string[]): DescriptionEntry[] =>
  descriptions.map((value) => ({ id: uuid(), value }));

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
}

export const ContractOptionsField = ({ eventId, event }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);

  const [hasOptions, setHasOptions] = useState(event.eventData.options?.hasOptions ?? false);
  const [entries, setEntries] = useState<DescriptionEntry[]>(() =>
    event.eventData.options?.descriptions?.length ? toEntries(event.eventData.options.descriptions) : [{ id: uuid(), value: "" }]
  );

  const saveOptions = useCallback(
    (enabled: boolean, items: DescriptionEntry[]) => {
      const filtered = items.map((e) => e.value).filter((d) => d.trim() !== "");
      saveField({ options: { value: { hasOptions: enabled, descriptions: filtered } } });
    },
    [saveField]
  );

  const debouncedSave = useDebouncedSave(saveOptions);

  const handleToggle = (enabled: boolean) => {
    setHasOptions(enabled);
    if (!enabled) {
      setEntries([{ id: uuid(), value: "" }]);
      debouncedSave.cancel();
      saveOptions(false, []);
    } else {
      saveOptions(true, entries);
    }
  };

  const handleDescriptionChange = (id: string, value: string) => {
    const updated = entries.map((e) => (e.id === id ? { ...e, value } : e));
    setEntries(updated);
    debouncedSave(true, updated);
  };

  const handleAdd = () => {
    setEntries([...entries, { id: uuid(), value: "" }]);
  };

  const handleRemove = (id: string) => {
    const updated = entries.filter((e) => e.id !== id);
    const result = updated.length === 0 ? [{ id: uuid(), value: "" }] : updated;
    setEntries(result);
    debouncedSave.cancel();
    saveOptions(true, result);
  };

  return (
    <FormControl>
      <Flex align="center" gap="4" mb={hasOptions ? "4" : "0"}>
        <FormLabel mb="0">{t("Contract options")}</FormLabel>
        <ButtonGroup isAttached size="sm">
          <Button
            colorScheme={hasOptions ? "teal" : "gray"}
            variant={hasOptions ? "solid" : "outline"}
            onClick={() => handleToggle(true)}
          >
            {t("Yes")}
          </Button>
          <Button
            colorScheme={!hasOptions ? "teal" : "gray"}
            variant={!hasOptions ? "solid" : "outline"}
            onClick={() => handleToggle(false)}
          >
            {t("No")}
          </Button>
        </ButtonGroup>
        {isSaving("options") && <Spinner size="xs" />}
      </Flex>

      {hasOptions && (
        <Box>
          <Flex direction="column" gap="2">
            {entries.map((entry) => (
              <Flex key={entry.id} gap="2" align="center">
                <Input
                  value={entry.value}
                  placeholder={t("Describe the option") ?? undefined}
                  onChange={(e) => handleDescriptionChange(entry.id, e.target.value)}
                />
                <IconButton
                  aria-label={t("Remove")}
                  icon={<FaTrash />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemove(entry.id)}
                />
              </Flex>
            ))}
          </Flex>
          <Button variant="ghost" colorScheme="teal" size="sm" mt="2" onClick={handleAdd}>
            + {t("Add option")}
          </Button>
        </Box>
      )}
    </FormControl>
  );
};
